import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Bed, Bath, Maximize, Shield, ArrowLeft, Check, 
  Phone, Mail, Calendar, ChevronLeft, ChevronRight, X,
  Building2, Play, Eye, Heart, Share2, Globe, TrendingUp, Users, FileImage
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getProperty, getProperties, getPropertyInsights, getPropertyMicromarket, getThumbnailUrl } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { MapContainer, TileLayer, Circle, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';

// Custom gold marker icon
const goldIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

function PropertyMap({ lat, lng, city }) {
  return (
    <MapContainer center={[lat, lng]} zoom={14} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false} zoomControl={true} attributionControl={false}>
      <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
      <Circle center={[lat, lng]} radius={800} pathOptions={{ color: '#D4AF37', fillColor: '#D4AF37', fillOpacity: 0.1, weight: 2 }} />
      <Marker position={[lat, lng]} icon={goldIcon} />
    </MapContainer>
  );
}

const PHONE_NUMBER = '+91 89995 70132';
const PHONE_LINK = 'tel:+918999570132';

// INR formatting for Indian properties
function formatINR(num) {
  if (!num) return 'Price on Request';
  if (num >= 10000000) {
    const cr = num / 10000000;
    return `\u20B9${cr % 1 === 0 ? cr : cr.toFixed(1)} Cr`;
  }
  if (num >= 100000) {
    const lakh = num / 100000;
    return `\u20B9${lakh % 1 === 0 ? lakh : lakh.toFixed(1)} Lac`;
  }
  return `\u20B9${Math.round(num).toLocaleString('en-IN')}`;
}

function formatINRSimple(num) {
  return formatINR(num);
}

// Browsing tracker - stores viewed properties and search context
const BROWSING_KEY = 'mw_browse_history';
const MAX_HISTORY = 20;

function getBrowsingHistory() {
  try {
    return JSON.parse(localStorage.getItem(BROWSING_KEY) || '[]');
  } catch { return []; }
}

function trackPropertyView(property) {
  const history = getBrowsingHistory().filter(h => h.id !== property.id);
  history.unshift({
    id: property.id,
    country: property.country,
    region: property.region,
    developer: property.developer || '',
    property_type: property.property_type,
    price_usd: property.price_usd,
    ts: Date.now()
  });
  localStorage.setItem(BROWSING_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

function scoreSimilarity(candidate, currentProperty, browsingHistory) {
  let score = 0;

  // Priority 1: Matches browsing behavior (high weight)
  const viewedCountries = browsingHistory.map(h => h.country);
  const viewedDevelopers = browsingHistory.map(h => h.developer).filter(Boolean);
  const viewedTypes = browsingHistory.map(h => h.property_type);
  
  const countryFreq = viewedCountries.filter(c => c === candidate.country).length;
  const devFreq = viewedDevelopers.filter(d => d === candidate.developer).length;
  const typeFreq = viewedTypes.filter(t => t === candidate.property_type).length;
  
  score += countryFreq * 10;
  score += devFreq * 8;
  score += typeFreq * 5;

  // Priority 2: Same developer as current property
  if (candidate.developer && candidate.developer === currentProperty.developer) score += 25;

  // Priority 3: Same country
  if (candidate.country === currentProperty.country) score += 15;

  // Priority 4: Same region
  if (candidate.region === currentProperty.region) score += 5;

  // Bonus for similar price range (within 50%)
  const priceDiff = Math.abs(candidate.price_usd - currentProperty.price_usd) / currentProperty.price_usd;
  if (priceDiff < 0.5) score += 3;

  return score;
}

export default function PropertyDetailPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [similarProperties, setSimilarProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [micromarket, setMicromarket] = useState(null);
  const [micromarketLoading, setMicromarketLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const dockRef = useRef(null);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch property data and all properties in parallel
        const [propertyData, allProperties] = await Promise.all([
          getProperty(propertyId),
          getProperties()
        ]);
        setProperty(propertyData);
        
        // Use cached insights if returned with property
        if (propertyData.ai_insights) {
          setAiInsights(propertyData.ai_insights);
        }
        
        // Track this view
        trackPropertyView(propertyData);
        const history = getBrowsingHistory().filter(h => h.id !== propertyId);
        
        // Score and sort by relevance
        const candidates = allProperties.filter(p => p.id !== propertyId);
        const scored = candidates.map(p => ({
          ...p,
          _score: scoreSimilarity(p, propertyData, history)
        }));
        scored.sort((a, b) => b._score - a._score);
        setSimilarProperties(scored.slice(0, 8));
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [propertyId]);

  // Lazy-load AI insights separately (non-blocking)
  useEffect(() => {
    if (!property || aiInsights) return;
    const fetchInsights = async () => {
      setInsightsLoading(true);
      try {
        const data = await getPropertyInsights(propertyId);
        if (data.status === 'ready' && data.insights) {
          setAiInsights(data.insights);
        }
      } catch {
        // Insights unavailable — not critical
      } finally {
        setInsightsLoading(false);
      }
    };
    fetchInsights();
  }, [property, propertyId, aiInsights]);

  // Lazy-load micromarket analysis (non-blocking)
  useEffect(() => {
    if (!property || micromarket) return;
    const fetchMicromarket = async () => {
      setMicromarketLoading(true);
      try {
        const data = await getPropertyMicromarket(propertyId);
        if (data.status === 'ready' && data.micromarket) {
          setMicromarket(data.micromarket);
        }
      } catch {
        // Micromarket unavailable — not critical
      } finally {
        setMicromarketLoading(false);
      }
    };
    fetchMicromarket();
  }, [property, propertyId, micromarket]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] pt-20 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-[#0B0C10] pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-white mb-4">Property not found</h2>
          <Link to="/properties"><Button className="btn-luxury">Back to Properties</Button></Link>
        </div>
      </div>
    );
  }

  // Filter out empty/falsy image URLs
  const validImages = (property.images || []).filter(url => url && url.trim());
  const images = validImages.length > 0 ? validImages : (property.image ? [property.image] : []);
  const isIndian = property.market === 'india' || property.country === 'India';
  const isProject = property.listing_type === 'project_villa' || property.listing_type === 'project_plot';
  
  // Smart price display - prefer price_inr for Indian, price_usd for international
  const displayPrice = isIndian && property.price_inr ? property.price_inr : property.price_usd;
  const displayPriceLabel = isIndian && property.price_inr ? 'INR' : null;
  
  const safeArea = property.area_sqft || property.land_area_sqft || 0;
  
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'location', label: 'Location' },
    { id: 'layout', label: 'Layout Plans' },
    ...(isIndian ? [] : [{ id: 'investment', label: 'Why Invest' }]),
    ...(isIndian ? [] : [{ id: 'rental', label: 'Rental Income' }]),
    { id: 'features', label: 'Features & Amenities' },
    ...(!isIndian && property?.visa_eligible ? [{ id: 'visa', label: 'Visa Information' }] : []),
    { id: 'developer', label: isIndian ? 'Developer' : 'Partner' },
  ];

  const openLightbox = (index) => { setLightboxIndex(index); setLightboxOpen(true); };

  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20 pb-28" data-testid="property-detail-page">
      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <button onClick={() => setLightboxOpen(false)} className="absolute top-6 right-6 text-white/70 hover:text-white z-50" data-testid="lightbox-close">
              <X className="h-8 w-8" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + images.length) % images.length); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <img src={images[lightboxIndex]} alt="" className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
            <button onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % images.length); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20">
              <ChevronRight className="h-6 w-6" />
            </button>
            <div className="absolute bottom-6 text-white/50 text-sm">{lightboxIndex + 1} / {images.length}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center text-sm text-[#A1A1AA]">
          <Link to="/properties" className="hover:text-white transition-colors flex items-center" data-testid="back-to-properties">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Properties
          </Link>
          <ChevronRight className="h-3 w-3 mx-2 text-[#A1A1AA]/50" />
          <span className="text-white/60">{property.country}</span>
          <ChevronRight className="h-3 w-3 mx-2 text-[#A1A1AA]/50" />
          <span className="text-white truncate max-w-[200px]">{property.title}</span>
        </div>
      </div>

      {/* Image Gallery - James Edition style */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        {images.length === 0 ? (
          <div className="rounded-2xl overflow-hidden h-[300px] sm:h-[400px] md:h-[480px] bg-[#1A1D24] border border-[#2A2F3A] flex items-center justify-center">
            <div className="text-center">
              <FileImage className="h-16 w-16 text-[#2A2F3A] mx-auto mb-3" />
              <p className="text-[#A1A1AA]">No images available for this property</p>
            </div>
          </div>
        ) : images.length === 1 ? (
          <div className="rounded-2xl overflow-hidden h-[300px] sm:h-[400px] md:h-[480px] cursor-pointer group" onClick={() => openLightbox(0)}>
            <img src={images[0]} alt={property.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 rounded-2xl overflow-hidden h-[300px] sm:h-[400px] md:h-[480px]">
          {/* Main large image */}
          <div className="md:col-span-2 md:row-span-2 relative cursor-pointer group" onClick={() => openLightbox(0)}>
            <img src={images[0]} alt={property.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
          </div>
          {/* Smaller grid images */}
          {images.slice(1, 5).map((img, i) => (
            <div key={i} className={`relative cursor-pointer group hidden md:block ${i === 3 ? 'relative' : ''}`} onClick={() => openLightbox(i + 1)}>
              <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              {i === 3 && images.length > 5 && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white text-lg font-medium">+{images.length - 5} Photos</span>
                </div>
              )}
            </div>
          ))}
        </div>
        )}
        {/* Gallery action bar */}
        <div className="flex items-center justify-between mt-3">
          <button onClick={() => openLightbox(0)} className="text-[#A1A1AA] hover:text-white text-sm flex items-center gap-1.5 transition-colors" data-testid="view-all-photos">
            <Eye className="h-4 w-4" /> View all {images.length} photos
          </button>
          <div className="flex items-center gap-3">
            <button className="text-[#A1A1AA] hover:text-white text-sm flex items-center gap-1.5 transition-colors">
              <Heart className="h-4 w-4" /> Save
            </button>
            <button className="text-[#A1A1AA] hover:text-white text-sm flex items-center gap-1.5 transition-colors">
              <Share2 className="h-4 w-4" /> Share
            </button>
          </div>
        </div>
      </section>

      {/* Price Bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pb-6 border-b border-[#2A2F3A]">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {property.visa_eligible && !isIndian && (
                <Badge className="bg-[#D4AF37] text-[#0B0C10]"><Shield className="h-3 w-3 mr-1" />{property.visa_type}</Badge>
              )}
              <Badge className="bg-[#1A1D24] text-white border border-[#2A2F3A]">{property.property_type}</Badge>
              <Badge className="bg-[#1A1D24] text-white border border-[#2A2F3A]">{property.region}</Badge>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl text-white leading-tight" data-testid="property-title">{property.title}</h1>
            <div className="flex items-center text-[#A1A1AA] mt-2 text-sm sm:text-base">
              <MapPin className="h-4 w-4 mr-1.5 text-[#D4AF37]" />
              {property.city}, {property.country}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">{isProject ? 'Starting Price' : 'Price'}</div>
            <div className="text-2xl sm:text-3xl text-[#D4AF37] font-semibold heading-display" data-testid="property-price">
              {isIndian && property.price_inr ? formatINR(property.price_inr) : formatPrice(property.price_usd)}
            </div>
            {property.price_range_display && <div className="text-[#A1A1AA] text-xs mt-1">{property.price_range_display}</div>}
          </div>
        </div>
      </section>

      {/* Quick Facts */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {(isProject ? [
            { icon: Building2, value: property.total_units || '-', label: 'Total Units' },
            { icon: Maximize, value: property.total_project_area || '-', label: 'Project Area' },
            { icon: Bed, value: (property.configurations || []).join(', ') || '-', label: 'Configurations' },
            { icon: MapPin, value: property.micromarket || property.city, label: 'Micromarket' },
          ] : [
            { icon: Bed, value: property.bedrooms || '-', label: 'Bedrooms' },
            { icon: Bath, value: property.bathrooms || '-', label: 'Bathrooms' },
            { icon: Maximize, value: safeArea ? `${safeArea.toLocaleString()} ft\u00B2` : '-', label: 'Living Area' },
            { icon: Building2, value: property.partner || property.developer || property.property_type, label: (property.partner || property.developer) ? (isIndian ? 'Developer' : 'Partner') : 'Type' },
          ]).map((fact, i) => (
            <div key={i} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 text-center">
              <fact.icon className="h-5 w-5 text-[#D4AF37] mx-auto mb-2" />
              <div className="text-white font-medium text-lg">{fact.value}</div>
              <div className="text-[#A1A1AA] text-xs uppercase tracking-wider">{fact.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Tabs + Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Tab Navigation */}
            <div className="flex overflow-x-auto border-b border-[#2A2F3A] mb-8 scrollbar-thin" data-testid="property-tabs" style={{ WebkitOverflowScrolling: 'touch' }}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors relative
                    ${activeTab === tab.id 
                      ? 'text-[#D4AF37]' 
                      : 'text-[#A1A1AA] hover:text-white'}`}
                  data-testid={`tab-${tab.id}`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]" />
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div data-testid="tab-content-overview">
                    <h2 className="text-xl text-white mb-4">About This Property</h2>
                    <p className="text-[#A1A1AA] leading-relaxed text-base mb-8">{property.description}</p>
                    
                    {/* Property Highlights */}
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-wider mb-1">Property Type</div>
                        <div className="text-white font-medium">{property.property_type}</div>
                      </div>
                      <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-wider mb-1">Region</div>
                        <div className="text-white font-medium">{property.region}</div>
                      </div>
                      <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-wider mb-1">Location</div>
                        <div className="text-white font-medium">{property.city}, {property.country}</div>
                      </div>
                      <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-wider mb-1">Living Area</div>
                        <div className="text-white font-medium">{safeArea ? `${safeArea.toLocaleString()} sq ft` : 'On request'}</div>
                      </div>
                    </div>

                    {/* Video Section Placeholder */}
                    <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl overflow-hidden mb-8">
                      <div className="aspect-video relative flex items-center justify-center bg-gradient-to-br from-[#1A1D24] to-[#0B0C10]">
                        {property.image && <img src={property.image} alt="" className="w-full h-full object-cover opacity-40" />}
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 border-2 border-[#D4AF37] flex items-center justify-center mb-3 cursor-pointer hover:bg-[#D4AF37]/30 transition-colors">
                            <Play className="h-7 w-7 text-[#D4AF37] ml-1" />
                          </div>
                          <span className="text-white/60 text-sm">Property Video Tour</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Location & Micromarket Tab */}
                {activeTab === 'location' && (
                  <div data-testid="tab-content-location">
                    {/* Map Section */}
                    {property.latitude && property.longitude && (
                      <div className="mb-8">
                        <h2 className="text-xl text-white mb-4">Approximate Location</h2>
                        <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl overflow-hidden" style={{ height: '350px' }}>
                          <PropertyMap lat={property.latitude} lng={property.longitude} city={property.city} />
                        </div>
                        <p className="text-[#A1A1AA]/50 text-xs mt-2">Approximate area shown. Exact location provided upon enquiry.</p>
                      </div>
                    )}

                    {/* Micromarket Analysis */}
                    {micromarket ? (
                      <>
                        <h2 className="text-xl text-white mb-4">About {micromarket.location_name || `${property.city}, ${property.country}`}</h2>
                        <p className="text-[#A1A1AA] leading-relaxed text-base mb-8">{micromarket.micromarket_brief}</p>

                        {/* Ratings */}
                        {micromarket.ratings && (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                            {[
                              { label: 'Investment Potential', score: micromarket.ratings.investment_potential, rationale: micromarket.ratings.investment_rationale, color: '#D4AF37' },
                              { label: 'Lifestyle Appeal', score: micromarket.ratings.lifestyle_appeal, rationale: micromarket.ratings.lifestyle_rationale, color: '#10B981' },
                              { label: 'Rental Income', score: micromarket.ratings.rental_income, rationale: micromarket.ratings.rental_rationale, color: '#3B82F6' },
                            ].map((item, i) => (
                              <div key={i} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl p-5">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-[#A1A1AA] text-sm">{item.label}</span>
                                  <span className="text-2xl font-bold" style={{ color: item.color }}>{item.score}<span className="text-sm text-[#A1A1AA]">/10</span></span>
                                </div>
                                <div className="w-full bg-[#2A2F3A] rounded-full h-2 mb-3">
                                  <div className="h-2 rounded-full transition-all duration-1000" style={{ width: `${item.score * 10}%`, backgroundColor: item.color }} />
                                </div>
                                <p className="text-[#A1A1AA]/70 text-xs leading-relaxed">{item.rationale}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Key Highlights */}
                        {micromarket.key_highlights?.length > 0 && (
                          <div className="mb-6">
                            <h3 className="text-lg text-white mb-4">Location Highlights</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {micromarket.key_highlights.map((h, i) => (
                                <div key={i} className="flex items-center p-3 bg-[#1A1D24] border border-[#2A2F3A] rounded-lg">
                                  <MapPin className="h-4 w-4 text-[#D4AF37] mr-3 flex-shrink-0" />
                                  <span className="text-[#A1A1AA] text-sm">{h}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-16">
                        {micromarketLoading ? (
                          <>
                            <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                            <p className="text-[#A1A1AA]">Generating location analysis...</p>
                            <p className="text-[#A1A1AA]/50 text-sm mt-1">AI is analyzing the micromarket for you</p>
                          </>
                        ) : (
                          !property.latitude && !property.longitude ? (
                            <p className="text-[#A1A1AA]">Location data not available for this property.</p>
                          ) : (
                            <p className="text-[#A1A1AA]">Micromarket analysis is not available.</p>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Investment Summary Tab */}
                {activeTab === 'investment' && (
                  <div data-testid="tab-content-investment">
                    {aiInsights?.investment_summary ? (
                      <>
                        {/* Headline */}
                        <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-2xl p-6 mb-8">
                          <div className="flex items-start gap-3">
                            <TrendingUp className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                            <p className="text-white text-lg leading-relaxed">{aiInsights.investment_summary.headline}</p>
                          </div>
                        </div>

                        {/* Why Invest */}
                        <h3 className="text-lg text-white mb-4">Why Should You Invest?</h3>
                        <div className="space-y-3 mb-8">
                          {aiInsights.investment_summary.why_invest?.map((point, i) => (
                            <div key={i} className="flex items-start p-4 bg-[#1A1D24] border border-[#2A2F3A] rounded-xl">
                              <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5 mr-3">
                                <span className="text-[#D4AF37] text-xs font-bold">{i + 1}</span>
                              </div>
                              <p className="text-[#A1A1AA] text-sm leading-relaxed">{point}</p>
                            </div>
                          ))}
                        </div>

                        {/* Lifestyle */}
                        <h3 className="text-lg text-white mb-4">Lifestyle Highlights</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                          {aiInsights.investment_summary.lifestyle_highlights?.map((point, i) => (
                            <div key={i} className="flex items-start p-4 bg-[#1A1D24] border border-[#2A2F3A] rounded-xl">
                              <Heart className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5 mr-3" />
                              <p className="text-[#A1A1AA] text-sm leading-relaxed">{point}</p>
                            </div>
                          ))}
                        </div>

                        {/* Ideal Investor */}
                        <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl p-6">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="h-5 w-5 text-[#D4AF37]" />
                            <h3 className="text-white font-medium">Ideal Investor Profile</h3>
                          </div>
                          <p className="text-[#A1A1AA] text-sm leading-relaxed">{aiInsights.investment_summary.ideal_investor_profile}</p>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-16">
                        {insightsLoading ? (
                          <>
                            <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                            <p className="text-[#A1A1AA]">Generating AI investment insights...</p>
                            <p className="text-[#A1A1AA]/50 text-sm mt-1">This may take a moment on first view</p>
                          </>
                        ) : (
                          <p className="text-[#A1A1AA]">AI investment insights are not available for this property.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Rental Income Projector Tab */}
                {activeTab === 'rental' && (
                  <div data-testid="tab-content-rental">
                    {aiInsights?.rental_projection ? (() => {
                      const r = aiInsights.rental_projection;
                      return (
                        <>
                          <div className="flex items-center gap-2 mb-6">
                            <h2 className="text-xl text-white">Airbnb Rental Income Projector</h2>
                            <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0 text-xs">AI Estimated</Badge>
                          </div>

                          {/* Key Metrics */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                            <div className="bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-xl p-4 text-center">
                              <div className="text-[#D4AF37] text-2xl font-semibold heading-display">${r.estimated_nightly_rate_usd}</div>
                              <div className="text-[#A1A1AA] text-xs mt-1">Avg/Night</div>
                            </div>
                            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 text-center">
                              <div className="text-white text-2xl font-semibold">{r.average_occupancy_percent}%</div>
                              <div className="text-[#A1A1AA] text-xs mt-1">Avg Occupancy</div>
                            </div>
                            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 text-center">
                              <div className="text-white text-2xl font-semibold">${(r.estimated_annual_revenue_usd || 0).toLocaleString()}</div>
                              <div className="text-[#A1A1AA] text-xs mt-1">Annual Revenue</div>
                            </div>
                            <div className="bg-gradient-to-br from-green-500/10 to-transparent border border-green-500/20 rounded-xl p-4 text-center">
                              <div className="text-green-400 text-2xl font-semibold">{r.net_annual_yield_percent}%</div>
                              <div className="text-[#A1A1AA] text-xs mt-1">Net Yield</div>
                            </div>
                          </div>

                          {/* Seasonal Rates */}
                          <h3 className="text-lg text-white mb-4">Seasonal Rate Breakdown</h3>
                          <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden mb-8">
                            <div className="grid grid-cols-3">
                              <div className="p-4 text-center border-r border-[#2A2F3A]">
                                <div className="text-[#A1A1AA] text-xs uppercase mb-1">Off-Season</div>
                                <div className="text-white text-lg font-medium">${r.off_season_rate_usd}/night</div>
                              </div>
                              <div className="p-4 text-center border-r border-[#2A2F3A] bg-[#D4AF37]/5">
                                <div className="text-[#D4AF37] text-xs uppercase mb-1">Average</div>
                                <div className="text-white text-lg font-medium">${r.estimated_nightly_rate_usd}/night</div>
                              </div>
                              <div className="p-4 text-center">
                                <div className="text-[#A1A1AA] text-xs uppercase mb-1">Peak Season</div>
                                <div className="text-white text-lg font-medium">${r.peak_season_rate_usd}/night</div>
                              </div>
                            </div>
                            {r.peak_season_months?.length > 0 && (
                              <div className="px-4 py-3 border-t border-[#2A2F3A] flex items-center justify-center gap-2">
                                <span className="text-[#A1A1AA] text-xs">Peak months:</span>
                                {r.peak_season_months.map((m, i) => (
                                  <Badge key={i} className="bg-[#D4AF37]/10 text-[#D4AF37] border-0 text-xs">{m}</Badge>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Financial Breakdown */}
                          <h3 className="text-lg text-white mb-4">Annual Financial Projection</h3>
                          <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden mb-8">
                            {[
                              ['Gross Annual Revenue', `$${(r.estimated_annual_revenue_usd || 0).toLocaleString()}`, 'text-[#D4AF37]'],
                              ['Estimated Annual Expenses', `- $${(r.estimated_annual_expenses_usd || 0).toLocaleString()}`, 'text-red-400'],
                              ['Net Annual Income', `$${((r.estimated_annual_revenue_usd || 0) - (r.estimated_annual_expenses_usd || 0)).toLocaleString()}`, 'text-green-400'],
                              ['Net Yield on Investment', `${r.net_annual_yield_percent}%`, 'text-green-400'],
                              ['Rental Demand', r.rental_demand_rating || 'N/A', r.rental_demand_rating === 'High' ? 'text-green-400' : 'text-yellow-400'],
                            ].map(([key, val, color], i) => (
                              <div key={i} className={`flex justify-between items-center px-5 py-3 ${i > 0 ? 'border-t border-[#2A2F3A]' : ''} ${i === 2 ? 'bg-[#D4AF37]/5' : ''}`}>
                                <span className="text-[#A1A1AA] text-sm">{key}</span>
                                <span className={`text-sm font-medium ${color}`}>{val}</span>
                              </div>
                            ))}
                          </div>

                          {/* Market Comparison */}
                          {r.market_comparison && (
                            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl p-5 mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Globe className="h-4 w-4 text-[#D4AF37]" />
                                <span className="text-white text-sm font-medium">Market Comparison</span>
                              </div>
                              <p className="text-[#A1A1AA] text-sm leading-relaxed">{r.market_comparison}</p>
                            </div>
                          )}

                          {/* Demand Drivers */}
                          {r.demand_drivers?.length > 0 && (
                            <div className="mb-4">
                              <h3 className="text-lg text-white mb-3">Rental Demand Drivers</h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {r.demand_drivers.map((driver, i) => (
                                  <div key={i} className="flex items-start p-3 bg-[#1A1D24] border border-[#2A2F3A] rounded-xl">
                                    <TrendingUp className="h-4 w-4 text-[#D4AF37] mr-3 flex-shrink-0 mt-0.5" />
                                    <span className="text-[#A1A1AA] text-sm">{driver}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Regulatory Notes */}
                          {r.regulatory_notes && (
                            <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-2xl p-5 mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Shield className="h-4 w-4 text-yellow-400" />
                                <span className="text-white text-sm font-medium">Regulatory Notes</span>
                              </div>
                              <p className="text-[#A1A1AA] text-sm leading-relaxed">{r.regulatory_notes}</p>
                            </div>
                          )}

                          <p className="text-[#A1A1AA]/40 text-xs mt-6 text-center">
                            * Projections are AI-generated estimates based on comparable market data. Actual returns may vary.
                          </p>
                        </>
                      );
                    })() : (
                      <div className="text-center py-16">
                        {insightsLoading ? (
                          <>
                            <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                            <p className="text-[#A1A1AA]">Generating rental projections...</p>
                            <p className="text-[#A1A1AA]/50 text-sm mt-1">This may take a moment on first view</p>
                          </>
                        ) : (
                          <p className="text-[#A1A1AA]">Rental projections are not available for this property.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Layout Plans Tab */}
                {activeTab === 'layout' && (
                  <div data-testid="tab-content-layout">
                    <h2 className="text-xl text-white mb-6">Layout Plans & Renders</h2>
                    {(property.layout_plans && property.layout_plans.length > 0) ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {property.layout_plans.map((url, index) => (
                          <div key={index} className="relative group rounded-xl overflow-hidden border border-[#2A2F3A] cursor-pointer" onClick={() => { setLightboxIndex(0); window.open(url, '_blank'); }}>
                            <img src={url} alt={`Layout Plan ${index + 1}`} className="w-full h-auto object-contain bg-[#1A1D24] min-h-[200px]" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                              <span className="opacity-0 group-hover:opacity-100 text-white text-sm bg-black/60 px-3 py-1 rounded-lg transition-opacity">Click to view full size</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : property.brochure_url ? (
                      <div>
                        <p className="text-[#A1A1AA] text-sm mb-4">Layout plans extracted from the project brochure:</p>
                        {(property.images && property.images.length > 1) ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {property.images.slice(1).map((url, index) => (
                              <div key={index} className="relative group rounded-xl overflow-hidden border border-[#2A2F3A] cursor-pointer" onClick={() => window.open(url, '_blank')}>
                                <img src={url} alt={`Brochure image ${index + 1}`} className="w-full h-auto object-contain bg-[#1A1D24] min-h-[200px]" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                  <span className="opacity-0 group-hover:opacity-100 text-white text-sm bg-black/60 px-3 py-1 rounded-lg transition-opacity">Click to view full size</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-8 text-center">
                            <FileImage className="h-12 w-12 text-[#2A2F3A] mx-auto mb-3" />
                            <p className="text-[#A1A1AA]">No layout images available in the brochure.</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-12 text-center">
                        <FileImage className="h-12 w-12 text-[#2A2F3A] mx-auto mb-3" />
                        <p className="text-[#A1A1AA] mb-2">Layout plans not yet available</p>
                        <p className="text-[#A1A1AA]/60 text-sm">The developer has not uploaded layout plans for this property.</p>
                      </div>
                    )}
                  </div>
                )}



                {/* Features Tab */}
                {activeTab === 'features' && (
                  <div data-testid="tab-content-features">
                    <h2 className="text-xl text-white mb-6">Features & Amenities</h2>
                    
                    {/* Standard features */}
                    {property.features?.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                        {property.features.map((feature, index) => (
                          <div key={index} className="flex items-center p-3 bg-[#1A1D24] border border-[#2A2F3A] rounded-lg">
                            <Check className="h-4 w-4 text-[#D4AF37] mr-3 flex-shrink-0" />
                            <span className="text-white text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Villa Amenities (Indian projects) */}
                    {property.villa_amenities?.length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-lg text-white mb-4">Villa Amenities</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {property.villa_amenities.map((a, i) => (
                            <div key={i} className="flex items-center p-3 bg-[#1A1D24] border border-[#2A2F3A] rounded-lg">
                              <Check className="h-4 w-4 text-[#D4AF37] mr-3 flex-shrink-0" />
                              <span className="text-white text-sm">{a}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Common Amenities (Indian projects) */}
                    {property.common_amenities?.length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-lg text-white mb-4">Common Amenities</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {property.common_amenities.map((a, i) => (
                            <div key={i} className="flex items-center p-3 bg-[#1A1D24] border border-[#2A2F3A] rounded-lg">
                              <Check className="h-4 w-4 text-[#D4AF37] mr-3 flex-shrink-0" />
                              <span className="text-white text-sm">{a}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Key Specs */}
                    <h3 className="text-lg text-white mb-4">Property Specifications</h3>
                    <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden">
                      {[
                        ['Property Type', property.property_type],
                        ...(isProject ? [
                          ['Listing Type', property.listing_type === 'project_villa' ? 'Project (Villas)' : property.listing_type === 'project_plot' ? 'Project (Plots)' : 'Individual Property'],
                          ['Total Units', property.total_units || '-'],
                          ['Project Area', property.total_project_area || '-'],
                          ['Configurations', (property.configurations || []).join(', ') || '-'],
                          ...(property.builtup_area_range ? [['Built-up Area Range', property.builtup_area_range]] : []),
                          ...(property.plot_area_range ? [['Plot Area Range', property.plot_area_range]] : []),
                          ...(property.rera_number ? [['RERA Number', property.rera_number]] : []),
                        ] : [
                          ['Bedrooms', property.bedrooms || '-'],
                          ['Bathrooms', property.bathrooms || '-'],
                          ['Area', safeArea ? `${safeArea.toLocaleString()} sq ft` : 'On request'],
                          ...(property.land_area_sqft ? [['Land Area', `${property.land_area_sqft.toLocaleString()} sq ft`]] : []),
                          ...(property.furnished_status ? [['Furnished Status', property.furnished_status]] : []),
                          ...(property.land_type ? [['Land Type', property.land_type]] : []),
                        ]),
                        ...(property.micromarket ? [['Micromarket', property.micromarket]] : []),
                        ['Country', property.country],
                        ['City', property.city],
                        ['Region', property.region],
                      ].map(([key, val], i) => (
                        <div key={i} className={`flex justify-between items-center px-5 py-3 ${i > 0 ? 'border-t border-[#2A2F3A]' : ''}`}>
                          <span className="text-[#A1A1AA] text-sm">{key}</span>
                          <span className="text-white text-sm font-medium">{val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Visa Tab */}
                {activeTab === 'visa' && (
                  <div data-testid="tab-content-visa">
                    {property.visa_eligible ? (
                      <>
                        <div className="p-6 bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 rounded-2xl mb-8">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                              <Shield className="h-6 w-6 text-[#D4AF37]" />
                            </div>
                            <div>
                              <h3 className="text-lg text-white mb-2">Residency Eligible</h3>
                              <p className="text-[#A1A1AA] leading-relaxed">
                                This property qualifies for the <span className="text-[#D4AF37] font-medium">{property.visa_type}</span> program 
                                in {property.country}. Minimum investment requirement: <span className="text-white font-medium">{formatPrice(property.min_investment)}</span>.
                              </p>
                            </div>
                          </div>
                        </div>

                        <h3 className="text-lg text-white mb-4">Visa Program Benefits</h3>
                        <div className="space-y-3 mb-8">
                          {[
                            'Residency permit for you and your family',
                            'Access to world-class healthcare and education',
                            'Freedom to travel within the region',
                            'Path to permanent residency or citizenship',
                            'Property can generate rental income',
                            'Full support from our residency experts',
                          ].map((benefit, i) => (
                            <div key={i} className="flex items-center p-3 bg-[#1A1D24] border border-[#2A2F3A] rounded-lg">
                              <Check className="h-4 w-4 text-[#D4AF37] mr-3 flex-shrink-0" />
                              <span className="text-white text-sm">{benefit}</span>
                            </div>
                          ))}
                        </div>

                        <Link to="/visas">
                          <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0C10]">
                            <Globe className="h-4 w-4 mr-2" /> Explore All Visa Programs
                          </Button>
                        </Link>
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <Shield className="h-12 w-12 text-[#A1A1AA]/30 mx-auto mb-4" />
                        <p className="text-[#A1A1AA]">This property is not currently eligible for visa programs.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Developer Tab */}
                {activeTab === 'developer' && (
                  <div data-testid="tab-content-developer">
                    <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl p-6 mb-8">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                          <Building2 className="h-7 w-7 text-[#D4AF37]" />
                        </div>
                        <div>
                          <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-0.5">Developed By</div>
                          <h3 className="text-xl text-white">{property.partner || property.developer || 'Independent Partner'}</h3>
                        </div>
                      </div>
                      <p className="text-[#A1A1AA] text-sm leading-relaxed">
                        This property is brought to you by {property.partner || property.developer || 'our trusted partner'} and is located in the 
                        premium {property.city} district of {property.country}. Contact us for more details about the partner&apos;s 
                        portfolio and track record.
                      </p>
                    </div>

                    <h3 className="text-lg text-white mb-4">Investment Highlights</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { icon: TrendingUp, title: 'Capital Appreciation', desc: 'Strong historical growth in property values' },
                        { icon: Globe, title: 'Global Accessibility', desc: 'Well-connected international destination' },
                        { icon: Shield, title: 'Secure Investment', desc: 'Regulated real estate market' },
                        { icon: Building2, title: 'Premium Construction', desc: 'High-quality finishes and materials' },
                      ].map((item, i) => (
                        <div key={i} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4">
                          <item.icon className="h-5 w-5 text-[#D4AF37] mb-2" />
                          <div className="text-white text-sm font-medium mb-1">{item.title}</div>
                          <div className="text-[#A1A1AA] text-xs">{item.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Sticky Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 space-y-6">
              {/* Contact Card */}
              <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl p-6">
                <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Listed Price</div>
                <div className="text-2xl text-[#D4AF37] font-semibold heading-display mb-5">
                  {isIndian && property.price_inr ? formatINR(property.price_inr) : formatPrice(property.price_usd)}
                </div>
                
                <div className="space-y-3 mb-5">
                  <Link to={`/contact?property_id=${property.id}&partner_id=${property.partner_id || ''}&property_title=${encodeURIComponent(property.title)}&destination=${encodeURIComponent(property.country)}`} className="block">
                    <Button className="w-full btn-luxury h-11" data-testid="enquire-btn">
                      <Mail className="h-4 w-4 mr-2" /> Enquire Now
                    </Button>
                  </Link>
                  <Button variant="outline" className="w-full border-[#2A2F3A] text-white hover:bg-[#2A2F3A] h-11">
                    <Calendar className="h-4 w-4 mr-2" /> Schedule Viewing
                  </Button>
                  <a href={PHONE_LINK} className="block">
                    <Button variant="outline" className="w-full border-[#2A2F3A] text-white hover:bg-[#2A2F3A] h-11">
                      <Phone className="h-4 w-4 mr-2" /> {PHONE_NUMBER}
                    </Button>
                  </a>
                </div>

                <div className="border-t border-[#2A2F3A] pt-5">
                  <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-3">Investment Highlights</div>
                  <ul className="space-y-2.5">
                    {(isIndian 
                      ? ['Prime location', 'High appreciation potential', 'End-to-end support', 'Verified developer']
                      : ['Visa-eligible property', 'Prime location', 'High rental potential', 'End-to-end support']
                    ).map((item, i) => (
                      <li key={i} className="flex items-center text-sm text-white">
                        <Check className="h-3.5 w-3.5 text-[#D4AF37] mr-2 flex-shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Agent Card */}
              <div className="bg-gradient-to-br from-[#D4AF37]/5 to-transparent border border-[#D4AF37]/20 rounded-2xl p-5">
                <div className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-3">Listed By</div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-semibold text-sm">MW</div>
                  <div>
                    <div className="text-white text-sm font-medium">Mulberry World</div>
                    <div className="text-[#A1A1AA] text-xs">Residency Experts</div>
                  </div>
                </div>
                <p className="text-[#A1A1AA] text-xs leading-relaxed">
                  Our team of residency experts guides Indian HNIs through every step of international property acquisition.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating macOS Dock - Similar Properties */}
      {similarProperties.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40" data-testid="similar-properties-dock">
          <div className="bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/95 to-transparent pt-8 pb-4">
            <div className="max-w-5xl mx-auto px-4">
              <div className="text-center mb-3">
                <span className="text-[#A1A1AA] text-xs uppercase tracking-widest">Similar Properties</span>
              </div>
              <div 
                ref={dockRef}
                className="flex items-end justify-center gap-2 sm:gap-3 overflow-x-auto scrollbar-hide px-4 py-1 dock-container"
              >
                {similarProperties.map((prop) => (
                  <DockItem key={prop.id} property={prop} formatPrice={formatPrice} navigate={navigate} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DockItem({ property, formatPrice, navigate }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      animate={{ 
        scale: hovered ? 1.15 : 1,
        y: hovered ? -8 : 0
      }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      onClick={() => navigate(`/properties/${property.id}`)}
      className="flex-shrink-0 cursor-pointer group"
      data-testid={`dock-item-${property.id}`}
    >
      <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 border-[#2A2F3A] group-hover:border-[#D4AF37] transition-colors shadow-lg shadow-black/40">
        <img src={getThumbnailUrl(property.image || (property.images && property.images[0]) || '', 100)} alt={property.title} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors" />
      </div>
      {/* Tooltip */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1A1D24] border border-[#2A2F3A] rounded-xl shadow-xl whitespace-nowrap z-50"
          >
            <div className="text-white text-xs font-medium truncate max-w-[180px]">{property.title}</div>
            <div className="text-[#D4AF37] text-xs">
              {(property.market === 'india' || property.country === 'India') && property.price_inr 
                ? formatINRSimple(property.price_inr) 
                : formatPrice(property.price_usd)}
            </div>
            <div className="text-[#A1A1AA] text-[10px]">{property.city}, {property.country}</div>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-[#1A1D24] border-r border-b border-[#2A2F3A] rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
